import React from 'react';

/** Pulsing bar skeleton for text / numbers */
function SkeletonBar({ className = '' }) {
  return <div className={`skeleton rounded ${className}`} />;
}

/** Skeleton for a KPI card */
function KPICardSkeleton() {
  return (
    <div className="card-modern p-6">
      <SkeletonBar className="h-3 w-24 mb-4" />
      <SkeletonBar className="h-9 w-32 mb-2" />
      <SkeletonBar className="h-3 w-20" />
    </div>
  );
}

/** Skeleton for a chart area */
function ChartSkeleton({ height = 'h-64' }) {
  return (
    <div className="card-modern p-6">
      <SkeletonBar className="h-5 w-48 mb-5" />
      <div className={`${height} bg-slate-50 rounded-xl animate-pulse flex items-end gap-2 px-4 pb-4`}>
        {[40, 65, 50, 80, 55, 70, 45, 60, 75, 50].map((h, i) => (
          <div key={i} className="flex-1 bg-slate-200 rounded-t" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

/** Skeleton for the full analytics dashboard */
export function AnalyticsSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8 animate-fade-in" data-testid="analytics-skeleton">
      {/* Header skeleton */}
      <div className="glass-card p-6 mb-8">
        <SkeletonBar className="h-8 w-96 mb-2" />
        <SkeletonBar className="h-4 w-64" />
      </div>

      {/* Filter bar skeleton */}
      <div className="card-modern p-5 mb-8">
        <div className="flex gap-3 mb-4">
          {[...Array(8)].map((_, i) => (
            <SkeletonBar key={i} className="h-9 w-20 rounded-full" />
          ))}
        </div>
        <div className="grid grid-cols-7 gap-4">
          {[...Array(7)].map((_, i) => (
            <SkeletonBar key={i} className="h-11 rounded-xl" />
          ))}
        </div>
      </div>

      {/* KPI cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {[...Array(4)].map((_, i) => <KPICardSkeleton key={i} />)}
      </div>

      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartSkeleton />
        <ChartSkeleton />
      </div>

      {/* Table skeleton */}
      <div className="card-modern p-6">
        <SkeletonBar className="h-6 w-48 mb-5" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4">
              <SkeletonBar className="h-4 w-24" />
              <SkeletonBar className="h-4 flex-1" />
              <SkeletonBar className="h-4 w-20" />
              <SkeletonBar className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Inline spinner for buttons */
export function ButtonSpinner({ size = 4 }) {
  return (
    <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

/** Overlay loader for modals / panels */
export function OverlayLoader({ message = 'Loading...' }) {
  return (
    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl" data-testid="overlay-loader">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-slate-200 border-t-primary mx-auto mb-3" />
        <p className="text-sm text-slate-700 font-semibold">{message}</p>
      </div>
    </div>
  );
}

/** Calculation History skeleton rows */
export function HistorySkeleton() {
  return (
    <div className="space-y-5 animate-fade-in" data-testid="history-skeleton">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="card-modern p-6">
          <div className="flex items-center gap-3 mb-4">
            <SkeletonBar className="h-6 w-56" />
            <SkeletonBar className="h-6 w-24 rounded-full" />
          </div>
          <SkeletonBar className="h-3 w-40 mb-4" />
          <div className="grid grid-cols-3 gap-5">
            <SkeletonBar className="h-4 w-full" />
            <SkeletonBar className="h-4 w-full" />
            <SkeletonBar className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
